import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

/** What the live region says while the host fetches the bytes. */
export const LIGHTBOX_STATUS_LOADING = 'Loading preview…';
/** What the live region says when the image could not be shown (the stage's error card says the same). */
export const LIGHTBOX_STATUS_UNAVAILABLE = "Preview isn't available for this file.";
/** What the live region says once the image is drawn. */
export const LIGHTBOX_STATUS_SHOWN = 'Image shown';

/**
 * The Tab step inside the open lightbox while it holds the focus (`focusOnOpen`): from
 * `current` (the index of the focused control; -1 = the focus is on none of them) to the
 * next of `count` controls, backwards for Shift+Tab, wrapping round. -1 = no control.
 */
export function nextLightboxFocusIndex(count: number, current: number, backwards: boolean): number {
    if (count <= 0) {
        return -1;
    }
    if (current < 0 || current >= count) {
        return backwards ? count - 1 : 0;
    }
    return backwards ? (current - 1 + count) % count : (current + 1) % count;
}

@Component({
    selector: 'vf-image-lightbox',
    templateUrl: './image-lightbox.component.html',
    styleUrls: ['./image-lightbox.component.scss'],
    // Encapsulation.None so styles still apply after the element is portaled to <body>.
    // All selectors use the `vf-lightbox-*` prefix to avoid global pollution.
    encapsulation: ViewEncapsulation.None
})
export class ImageLightboxComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input() open: boolean = false;
    @Input() imageUrl: string = '';
    @Input() fileName: string = '';
    @Input() fileSize: number = 0;
    /** File extension (no leading dot, lowercased). When set to a non-image
     *  extension, the lightbox renders a generic file icon card instead of
     *  trying to load `imageUrl` as an <img>. */
    @Input() fileExtension: string = '';
    /** Optional URL opened by the "Open" button shown for non-image files. */
    @Input() webUrl: string = '';
    /** The host is still resolving `imageUrl` (e.g. fetching the bytes through the
     *  OneDrive proxy). Hosts open the lightbox immediately for responsiveness, so
     *  without this the stage would render its "not available" state during the
     *  fetch and flash to the image a moment later. */
    @Input() loading: boolean = false;
    /** Opt-in keyboard-modal mode: each time the lightbox opens, move the focus to
     *  its Close button, and while it is open keep Tab / Shift+Tab on its own
     *  controls (Close, then the error card's link or the file card's Open), so a
     *  keyboard or screen-reader user lands inside the dialog and cannot walk into
     *  the page under the dim. Off by default (no focus move, no Tab listener); a
     *  host that turns it on gives the focus back itself on close
     *  (vf-comment-image-preview does). */
    @Input() focusOnOpen: boolean = false;

    @Output() closed: EventEmitter<void> = new EventEmitter<void>();

    /** Image extensions the lightbox actually renders inline. Anything else
     *  falls back to the icon-card mode (per the spec for the new mention
     *  file chips). */
    private static readonly IMAGE_EXTS: string[] = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];

    @ViewChild('backdropEl', { static: true }) backdropEl: ElementRef<HTMLDivElement> | null = null;
    /** The Close button; drawn only while open, so read after the open render. */
    @ViewChild('closeBtn') closeBtn: ElementRef<HTMLButtonElement> | null = null;
    /** The error card's "Open in new tab" link, when drawn (a Tab stop of `focusOnOpen`). */
    @ViewChild('errorLink') errorLink: ElementRef<HTMLAnchorElement> | null = null;
    /** The file card's "Open" button, when drawn (a Tab stop of `focusOnOpen`). */
    @ViewChild('fileOpenBtn') fileOpenBtn: ElementRef<HTMLButtonElement> | null = null;

    /** The dialog's accessible name ("Image preview: ‹name›"), set in ngOnChanges. */
    public dialogLabel: string = 'Image preview';
    /** What the polite live region tells a screen reader about the stage (loading, shown, not
     *  available); '' = nothing. Set in ngOnChanges and when the <img> fails. */
    public statusText: string = '';

    /** Set when the <img> fails to load — usually because the SharePoint/OneDrive
     *  webUrl points to a viewer page rather than raw image bytes. */
    public imageLoadFailed: boolean = false;

    /** SafeUrl wrapper for the current imageUrl. Angular's URL sanitizer marks
     *  `blob:` URLs as unsafe by default, breaking [src] binding — bypass for
     *  the lightbox the same way the upload-modal preview does. */
    public safeImageUrl: SafeUrl | null = null;

    private _portaled: boolean = false;
    /** The tick that focuses the Close button once the open lightbox is drawn (`focusOnOpen`). */
    private _focusTimer: ReturnType<typeof setTimeout> | null = null;
    /** Removes the document keydown listener that keeps Tab inside (`focusOnOpen`; bound
     *  only while open). null = none bound. */
    private _unlistenTab: (() => void) | null = null;

    constructor(private _sanitizer: DomSanitizer, private _renderer: Renderer2) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Reset the failure flag each time the lightbox is opened with a (possibly new) image
        if ((changes['open'] && this.open) || changes['imageUrl']) {
            this.imageLoadFailed = false;
        }
        if (changes['open'] && this.open && this.focusOnOpen) {
            this._focusCloseSoon();
        }
        if (changes['imageUrl']) {
            // Bypass URL sanitization for blob: URLs (Angular doesn't whitelist them).
            this.safeImageUrl = this.imageUrl
                ? this._sanitizer.bypassSecurityTrustUrl(this.imageUrl)
                : null;
        }
        this._syncTabTrap();
        this.dialogLabel = this._buildDialogLabel();
        this._refreshStatus();
    }

    ngAfterViewInit(): void {
        this._portalToBody();
    }

    ngOnDestroy(): void {
        this._clearFocusTimer();
        this._stopTabTrap();
        this._removeFromBody();
    }

    /** Bind the Tab listener while open in keyboard-modal mode (`focusOnOpen`), and only then. */
    private _syncTabTrap(): void {
        const wanted: boolean = this.open && this.focusOnOpen;

        if (wanted && !this._unlistenTab) {
            this._unlistenTab = this._renderer.listen('document', 'keydown', (event: KeyboardEvent) =>
                this._keepFocusInside(event)
            );
        } else if (!wanted) {
            this._stopTabTrap();
        }
    }

    private _stopTabTrap(): void {
        const unlisten: (() => void) | null = this._unlistenTab;

        this._unlistenTab = null;
        if (unlisten) {
            unlisten();
        }
    }

    /** Tab / Shift+Tab while open: move to the next of the lightbox's own controls, not into
     *  the page under the dim. */
    private _keepFocusInside(event: KeyboardEvent): void {
        let targets: HTMLElement[] = [];
        let index = -1;

        if (!event || event.key !== 'Tab' || !this.open || !this.focusOnOpen) {
            return;
        }
        targets = this._focusTargets();
        index = nextLightboxFocusIndex(targets.length, targets.indexOf(event.target as HTMLElement), event.shiftKey);
        if (index < 0) {
            return;
        }
        event.preventDefault();
        targets[index].focus({ preventScroll: true });
    }

    /** The lightbox's controls drawn now, in Tab order: Close, then the error card's link or
     *  the file card's Open. */
    private _focusTargets(): HTMLElement[] {
        const refs: Array<ElementRef<HTMLElement> | null> = [this.closeBtn, this.errorLink, this.fileOpenBtn];
        const targets: HTMLElement[] = [];

        refs.forEach((ref: ElementRef<HTMLElement> | null) => {
            if (ref && ref.nativeElement) {
                targets.push(ref.nativeElement);
            }
        });
        return targets;
    }

    private _buildDialogLabel(): string {
        const kind: string = this.isImageFile ? 'Image preview' : 'File preview';

        return this.fileName ? kind + ': ' + this.fileName : kind;
    }

    /** The live region's text for the stage now drawn (image mode only; the file card is
     *  announced by its name). */
    private _refreshStatus(): void {
        let text = '';

        if (this.open && this.isImageFile) {
            if (this.loading) {
                text = LIGHTBOX_STATUS_LOADING;
            } else if (this.safeImageUrl && !this.imageLoadFailed) {
                text = LIGHTBOX_STATUS_SHOWN;
            } else {
                text = LIGHTBOX_STATUS_UNAVAILABLE;
            }
        }
        this.statusText = text;
    }

    /** Focus the Close button after this change-detection pass has drawn it (it is under *ngIf="open"). */
    private _focusCloseSoon(): void {
        this._clearFocusTimer();
        this._focusTimer = setTimeout(() => {
            const btn: HTMLButtonElement | undefined = this.closeBtn?.nativeElement;

            this._focusTimer = null;
            if (this.open && btn) {
                btn.focus({ preventScroll: true });
            }
        });
    }

    private _clearFocusTimer(): void {
        if (this._focusTimer !== null) {
            clearTimeout(this._focusTimer);
            this._focusTimer = null;
        }
    }

    /** Move the backdrop to <body> so it covers the viewport regardless of ancestor transforms. */
    private _portalToBody(): void {
        if (this._portaled) {
            return;
        }
        const el = this.backdropEl?.nativeElement;
        if (el && document.body) {
            document.body.appendChild(el);
            this._portaled = true;
        }
    }

    private _removeFromBody(): void {
        const el = this.backdropEl?.nativeElement;
        if (el && el.parentNode === document.body) {
            try { document.body.removeChild(el); } catch { /* ignore */ }
        }
    }

    /** True when the lightbox should attempt to render `imageUrl` as an inline
     *  image. When false (any non-image extension), the icon-card fallback is
     *  shown instead. Image extension lookup is case-insensitive. */
    public get isImageFile(): boolean {
        if (!this.fileExtension) {
            // Backwards compatibility: when callers don't supply an extension,
            // treat the lightbox as image-only (the original behavior).
            return true;
        }
        const ext: string = (this.fileExtension || '').toLowerCase().replace(/^\./, '');
        return ImageLightboxComponent.IMAGE_EXTS.indexOf(ext) >= 0;
    }

    /** Opens the file in a new tab using `webUrl` (falls back to `imageUrl`).
     *  Wired up to the "Open" action shown for non-image files. */
    public openExternal(): void {
        const url: string = this.webUrl || this.imageUrl || '';
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }

    public formatSize(bytes: number): string {
        let size = 0;
        if (!bytes || bytes <= 0) {
            return '';
        }
        if (bytes < 1024) {
            return bytes + ' B';
        }
        if (bytes < 1024 * 1024) {
            size = bytes / 1024;
            return Math.round(size) + ' KB';
        }
        size = bytes / (1024 * 1024);
        return size.toFixed(1) + ' MB';
    }

    public footerText(): string {
        const sizeText = this.formatSize(this.fileSize);
        if (this.fileName && sizeText) {
            return this.fileName + ' · ' + sizeText;
        }
        return this.fileName || sizeText || '';
    }

    public onBackdropClick(ev: MouseEvent): void {
        if (ev.target === this.backdropEl?.nativeElement) {
            this.close();
        }
    }

    public close(): void {
        this.closed.emit();
    }

    public onImageError(): void {
        this.imageLoadFailed = true;
        this._refreshStatus();
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        if (this.open) {
            this.close();
        }
    }
}
